/**
 * Main Navigation — frontend script.
 *
 * Handles several independent interactive pieces inside `.b-main-navigation`:
 * - Desktop mega-menu dropdowns (hover + keyboard, closes on Escape/outside
 * click, with a short close delay so crossing the detached dropdown's gap
 * doesn't close it prematurely).
 * - Mobile hamburger panel (toggle, Escape to close, focus returns to the trigger).
 * - Mobile accordion behaviour for nav items that own a mega-menu.
 * - WPML language switcher dropdown (hover to open, plus click/keyboard toggle).
 * - Scrolled state: adds `--scrolled` once scrolled past a small threshold,
 * animating the nav bar's `top` from its resting offset down to 2.25rem.
 * - Topbar height sync: keeps `--topbar-height` matched to the topbar's
 * real rendered height (it can wrap onto two lines on narrow viewports).
 */

document.querySelectorAll( '.b-main-navigation' ).forEach( ( nav ) => {

	initMegaMenus( nav );
	initMobilePanel( nav );
	initLanguageSwitcher( nav );
	initScrolledState( nav );
	initTopbarHeightSync( nav );

} );

/**
 * Wire up hover/focus/keyboard behaviour for each top-level nav item that
 * owns a `.b-main-navigation__mega-menu` dropdown.
 *
 * @param {HTMLElement} nav Root `.b-main-navigation` element.
 * @return {void}
 */
function initMegaMenus( nav ) {

	const CLOSE_DELAY = 200;
	const items = nav.querySelectorAll( '.b-main-navigation__nav-item' );

	items.forEach( ( item ) => {

		const link = item.querySelector( ':scope > .b-main-navigation__nav-link' );
		const megaMenu = item.querySelector( ':scope > .b-main-navigation__mega-menu' );

		if ( ! link || ! megaMenu ) {

			return;

		}

		link.setAttribute( 'aria-haspopup', 'true' );
		link.setAttribute( 'aria-expanded', 'false' );
		item.classList.add( 'has-mega-menu' );

		let closeTimeout = null;

		/**
		 * Cancel any pending delayed close for this item.
		 *
		 * @return {void}
		 */
		const cancelPendingClose = () => {

			if ( closeTimeout ) {

				window.clearTimeout( closeTimeout );
				closeTimeout = null;

			}

		};

		/**
		 * Close this item's mega-menu.
		 *
		 * @return {void}
		 */
		const closeMenu = () => {

			cancelPendingClose();
			item.classList.remove( 'is-open' );
			link.setAttribute( 'aria-expanded', 'false' );

		};

		/**
		 * Schedule closing this item's mega-menu after a short grace delay,
		 * so briefly crossing the visual gap between the nav link and the
		 * detached dropdown (e.g. moving the mouse diagonally) doesn't
		 * close it prematurely.
		 *
		 * @return {void}
		 */
		const scheduleClose = () => {

			cancelPendingClose();
			closeTimeout = window.setTimeout( closeMenu, CLOSE_DELAY );

		};

		/**
		 * Open this item's mega-menu, closing any other open ones first.
		 *
		 * @return {void}
		 */
		const openMenu = () => {

			cancelPendingClose();
			closeSiblingMenus( items, item );
			item.classList.add( 'is-open' );
			link.setAttribute( 'aria-expanded', 'true' );

		};

		// Only the link itself opens the menu on hover — the invisible
		// `::after` bridge below it (and the detached dropdown) must not
		// trigger opening on their own, they only keep it open once it is.
		link.addEventListener( 'mouseenter', () => {

			if ( ! isMobileViewport() ) {

				openMenu();

			}

		} );

		item.addEventListener( 'mouseenter', () => {

			if ( ! isMobileViewport() ) {

				cancelPendingClose();

			}

		} );

		item.addEventListener( 'mouseleave', () => {

			if ( ! isMobileViewport() ) {

				scheduleClose();

			}

		} );

		link.addEventListener( 'focus', () => {

			if ( ! isMobileViewport() ) {

				openMenu();

			}

		} );

		item.addEventListener( 'keydown', ( event ) => {

			if ( event.key === 'Escape' ) {

				closeMenu();
				link.focus();

			}

		} );

	} );

	nav.addEventListener( 'focusout', ( event ) => {

		if ( isMobileViewport() ) {

			return;

		}

		const relatedItem = event.relatedTarget ? event.relatedTarget.closest( '.b-main-navigation__nav-item' ) : null;

		items.forEach( ( item ) => {

			if ( item !== relatedItem ) {

				item.classList.remove( 'is-open' );

				const link = item.querySelector( ':scope > .b-main-navigation__nav-link' );

				if ( link ) {

					link.setAttribute( 'aria-expanded', 'false' );

				}

			}

		} );

	} );

}

/**
 * Close every open mega-menu except the one currently being opened.
 *
 * @param {NodeListOf<HTMLElement>} items       All top-level nav items.
 * @param {HTMLElement}             currentItem Item that should stay open.
 * @return {void}
 */
function closeSiblingMenus( items, currentItem ) {

	items.forEach( ( item ) => {

		if ( item === currentItem ) {

			return;

		}

		item.classList.remove( 'is-open' );

		const link = item.querySelector( ':scope > .b-main-navigation__nav-link' );

		if ( link ) {

			link.setAttribute( 'aria-expanded', 'false' );

		}

	} );

}

/**
 * Wire up the mobile hamburger toggle: opens/closes the slide-in panel,
 * locks body scroll while open, and restores focus to the trigger on close.
 *
 * @param {HTMLElement} nav Root `.b-main-navigation` element.
 * @return {void}
 */
function initMobilePanel( nav ) {

	const toggle = nav.querySelector( '.b-main-navigation__burger' );

	// Look up the panel by DOM structure rather than `aria-controls`: some
	// rendering paths (e.g. Block Area content sanitization) can strip
	// non-essential ARIA attributes from the `<button>` markup, and the
	// panel/toggle relationship must keep working even then.
	const panel = nav.querySelector( '.b-main-navigation__mobile-panel' );

	if ( ! toggle || ! panel ) {

		return;

	}

	// Restore the `aria-controls`/`id` pairing at runtime for assistive tech,
	// in case it was stripped from the server-rendered markup.
	if ( ! panel.id ) {

		panel.id = 'main-navigation-panel';

	}

	toggle.setAttribute( 'aria-controls', panel.id );

	/**
	 * Hide the mobile panel and reset its trigger/lock state.
	 *
	 * @return {void}
	 */
	const closePanel = () => {

		panel.setAttribute( 'hidden', '' );
		toggle.setAttribute( 'aria-expanded', 'false' );
		nav.classList.remove( 'b-main-navigation--panel-open' );
		document.body.classList.remove( 'has-main-navigation-panel-open' );

	};

	/**
	 * Show the mobile panel and lock body scroll.
	 *
	 * @return {void}
	 */
	const openPanel = () => {

		panel.removeAttribute( 'hidden' );
		toggle.setAttribute( 'aria-expanded', 'true' );
		nav.classList.add( 'b-main-navigation--panel-open' );
		document.body.classList.add( 'has-main-navigation-panel-open' );

	};

	toggle.addEventListener( 'click', () => {

		const isOpen = toggle.getAttribute( 'aria-expanded' ) === 'true';

		if ( isOpen ) {

			closePanel();

		} else {

			openPanel();

		}

	} );

	document.addEventListener( 'keydown', ( event ) => {

		if ( event.key === 'Escape' && toggle.getAttribute( 'aria-expanded' ) === 'true' ) {

			closePanel();
			toggle.focus();

		}

	} );

	// Accordion behaviour: tapping a nav item that owns a mega-menu expands
	// it in place instead of navigating (real navigation still works for
	// menu items without children).
	panel.querySelectorAll( '.b-main-navigation__nav-item' ).forEach( ( item ) => {

		const link = item.querySelector( ':scope > .b-main-navigation__nav-link' );
		const megaMenu = item.querySelector( ':scope > .b-main-navigation__mega-menu' );

		if ( ! link || ! megaMenu ) {

			return;

		}

		link.setAttribute( 'aria-haspopup', 'true' );
		link.setAttribute( 'aria-expanded', 'false' );

		link.addEventListener( 'click', ( event ) => {

			event.preventDefault();

			const isOpen = item.classList.contains( 'is-open' );

			item.classList.toggle( 'is-open', ! isOpen );
			link.setAttribute( 'aria-expanded', String( ! isOpen ) );

		} );

	} );

}

/**
 * Wire up the WPML language switcher dropdown in the desktop utility pill.
 *
 * @param {HTMLElement} nav Root `.b-main-navigation` element.
 * @return {void}
 */
function initLanguageSwitcher( nav ) {

	const CLOSE_DELAY = 80;
	const switcher = nav.querySelector( '.b-main-navigation__lang-switcher' );
	const toggle = switcher ? switcher.querySelector( '.b-main-navigation__lang-toggle' ) : null;

	if ( ! switcher || ! toggle ) {

		return;

	}

	let closeTimeout = null;

	/**
	 * Cancel any pending delayed close.
	 *
	 * @return {void}
	 */
	const cancelPendingClose = () => {

		if ( closeTimeout ) {

			window.clearTimeout( closeTimeout );
			closeTimeout = null;

		}

	};

	/**
	 * Open the language switcher dropdown.
	 *
	 * @return {void}
	 */
	const openSwitcher = () => {

		cancelPendingClose();
		switcher.classList.add( 'is-open' );
		toggle.setAttribute( 'aria-expanded', 'true' );

	};

	/**
	 * Close the language switcher dropdown.
	 *
	 * @return {void}
	 */
	const closeSwitcher = () => {

		cancelPendingClose();
		switcher.classList.remove( 'is-open' );
		toggle.setAttribute( 'aria-expanded', 'false' );

	};

	/**
	 * Schedule closing the dropdown after a short grace delay, so briefly
	 * crossing the visual gap between the toggle and the detached dropdown
	 * doesn't close it prematurely.
	 *
	 * @return {void}
	 */
	const scheduleClose = () => {

		cancelPendingClose();
		closeTimeout = window.setTimeout( closeSwitcher, CLOSE_DELAY );

	};

	toggle.addEventListener( 'click', () => {

		if ( switcher.classList.contains( 'is-open' ) ) {

			closeSwitcher();

		} else {

			openSwitcher();

		}

	} );

	// Hover support (mouse users) in addition to the click toggle above
	// (keyboard/touch users). Matches the mega-menu's hover behaviour.
	switcher.addEventListener( 'mouseenter', () => {

		if ( ! isMobileViewport() ) {

			openSwitcher();

		}

	} );

	switcher.addEventListener( 'mouseleave', () => {

		if ( ! isMobileViewport() ) {

			scheduleClose();

		}

	} );

	document.addEventListener( 'click', ( event ) => {

		if ( ! switcher.contains( event.target ) ) {

			closeSwitcher();

		}

	} );

	switcher.addEventListener( 'keydown', ( event ) => {

		if ( event.key === 'Escape' ) {

			closeSwitcher();
			toggle.focus();

		}

	} );

}

/**
 * Whether the viewport currently matches the mobile layout (below `lg`).
 *
 * @return {boolean} True when the mobile layout is active.
 */
function isMobileViewport() {

	return window.matchMedia( '(max-width: 991.98px)' ).matches;

}

/**
 * Toggle the `b-main-navigation--scrolled` modifier once the page has been
 * scrolled past the topbar's own height (or a small default threshold when
 * there's no topbar), and remove it again near the top. The nav bar rests
 * slightly lower on load (matches the Figma design) and animates up to
 * `top: 2.25rem` via CSS transition when this class is added — i.e. it
 * locks into place right as the topbar scrolls out of view.
 *
 * @param {HTMLElement} nav Root `.b-main-navigation` element.
 * @return {void}
 */
function initScrolledState( nav ) {

	const DEFAULT_THRESHOLD = 10;

	// The topbar (when enabled) renders as the nav's immediately preceding
	// sibling — see template-parts/blocks/main-navigation.php.
	const topbarEl = nav.previousElementSibling;
	let ticking = false;

	/**
	 * The current scroll threshold: the topbar's live rendered height (it
	 * changes across breakpoints), or a small default when there's none.
	 *
	 * @return {number} Threshold in pixels.
	 */
	const getScrollThreshold = () => {

		return topbarEl && topbarEl.classList.contains( 'b-main-navigation__topbar' )
			? topbarEl.offsetHeight
			: DEFAULT_THRESHOLD;

	};

	/**
	 * Read the current scroll position and update the modifier class.
	 *
	 * @return {void}
	 */
	const updateScrolledState = () => {

		nav.classList.toggle( 'b-main-navigation--scrolled', window.scrollY > getScrollThreshold() );
		ticking = false;

	};

	window.addEventListener( 'scroll', () => {

		if ( ! ticking ) {

			window.requestAnimationFrame( updateScrolledState );
			ticking = true;

		}

	}, { passive: true } );

	// Set the correct initial state immediately (e.g. page reloaded mid-scroll).
	updateScrolledState();

}

/**
 * Keep the `--topbar-height` custom property in sync with the topbar's real
 * rendered height. The static value set in `_root.scss` assumes a single
 * line of text; on narrow viewports the announcement text can wrap onto a
 * second line, making the topbar taller than that fixed value. Since the
 * nav bar's resting `top` and the mobile panel's `top`/`height` are derived
 * from `--topbar-height`, an out-of-date value causes the mobile panel to
 * fall short of (or overshoot) the viewport's bottom edge.
 *
 * @param {HTMLElement} nav Root `.b-main-navigation` element.
 * @return {void}
 */
function initTopbarHeightSync( nav ) {

	const topbarEl = nav.previousElementSibling;

	if ( ! topbarEl || ! topbarEl.classList.contains( 'b-main-navigation__topbar' ) ) {

		return;

	}

	const syncHeight = () => {

		document.documentElement.style.setProperty( '--topbar-height', `${ topbarEl.offsetHeight }px` );

	};

	if ( 'ResizeObserver' in window ) {

		new ResizeObserver( syncHeight ).observe( topbarEl );

	} else {

		window.addEventListener( 'resize', syncHeight, { passive: true } );

	}

	syncHeight();

}
